/**
 * Purchasing transport boundary.
 *
 * Every request/response alias comes from the generated canonical OpenAPI
 * client — no wire type is hand-written here. The app's HTTP layer keeps
 * owning credentials, idempotency replay and ETag headers; this module only
 * binds those mechanics to the purchasing operation types.
 *
 * Idempotency and concurrency follow openapi/generated/route_policy.gen.go
 * exactly:
 *   client-key + If-Match : submit, send, close, landed-cost post
 *   client-key            : create PO, create GRN, create landed cost,
 *                           create supplier item, convert, dismiss
 *   If-Match only         : PUT purchase order, PATCH landed cost,
 *                           PUT/DELETE supplier item
 */
import type { components, operations } from "@erp/api-client";

import { apiFetch, apiRequest, apiFetchOperational, type Page } from "@/lib/api/http";
import { fetchWithEtag, type WithEtag } from "@/features/shared/ops-api";

type Schemas = components["schemas"];

// ── Wire types ───────────────────────────────────────────────────────────

export type PurchaseOrderStatus = Schemas["PurchasingPurchaseOrderStatus"];
export type PurchaseOrder = Schemas["PurchasingPurchaseOrder"];
export type PurchaseOrderSummary = Schemas["PurchasingPurchaseOrderSummary"];
export type PurchaseOrderLine = Schemas["PurchasingPurchaseOrderLine"];
export type PurchaseOrderLineInput = Schemas["PurchasingPurchaseOrderLineInput"];
export type PurchaseOrderCreate = Schemas["PurchasingPurchaseOrderCreate"];
export type PurchaseOrderUpdate = Schemas["PurchasingPurchaseOrderUpdate"];
export type SupplierItemHint = Schemas["PurchasingSupplierItemHint"];
export type ApprovalPreview = Schemas["PurchasingApprovalPreview"];
export type SendPurchaseOrderRequest = Schemas["PurchasingSendPurchaseOrderRequest"];
export type SendPurchaseOrderResult = Schemas["PurchasingSendPurchaseOrderResult"];
export type ClosePurchaseOrderRequest = Schemas["PurchasingClosePurchaseOrderRequest"];
export type Transmission = Schemas["PurchasingTransmission"];
export type TransmissionStatus = Schemas["PurchasingTransmissionStatus"];

export type Grn = Schemas["PurchasingGrn"];
export type GrnSummary = Schemas["PurchasingGrnSummary"];
export type GrnLine = Schemas["PurchasingGrnLine"];
export type GrnLineInput = Schemas["PurchasingGrnLineInput"];
export type GrnCreate = Schemas["PurchasingGrnCreate"];
export type GrnCreateResult = Schemas["PurchasingGrnCreateResult"];

export type LandedCostStatus = Schemas["PurchasingLandedCostStatus"];
export type LandedCostType = Schemas["PurchasingLandedCostType"];
export type LandedCostBasis = Schemas["PurchasingLandedCostBasis"];
export type LandedCostDocument = Schemas["PurchasingLandedCostDocument"];
export type LandedCostDocumentSummary = Schemas["PurchasingLandedCostDocumentSummary"];
export type LandedCostDocumentCreate = Schemas["PurchasingLandedCostDocumentCreate"];
export type LandedCostDocumentPatch = Schemas["PurchasingLandedCostDocumentPatch"];
export type LandedCostLineInput = Schemas["PurchasingLandedCostLineInput"];
export type LandedCostPreview = Schemas["PurchasingLandedCostPreview"];
export type LandedCostPreviewAllocation = Schemas["PurchasingLandedCostPreviewAllocation"];
export type LandedCostPreviewRequest = Schemas["PurchasingLandedCostPreviewRequest"];
export type LandedCostPostRequest = Schemas["PurchasingLandedCostPostRequest"];
export type LandedCostPostResult = Schemas["PurchasingLandedCostPostResult"];

export type SupplierItem = Schemas["PurchasingSupplierItem"];
export type SupplierItemCreate = Schemas["PurchasingSupplierItemCreate"];
export type SupplierItemUpdate = Schemas["PurchasingSupplierItemUpdate"];

export type ReorderSuggestion = Schemas["PurchasingReorderSuggestion"];
export type ReorderSuggestionStatus = Schemas["PurchasingReorderSuggestionStatus"];
export type ReorderConversionRequest = Schemas["PurchasingReorderConversionRequest"];
export type ReorderConversionResult = Schemas["PurchasingReorderConversionResult"];
export type ReorderDismissRequest = Schemas["PurchasingReorderDismissRequest"];
export type ReorderDismissResult = Schemas["PurchasingReorderDismissResult"];
export type ReorderDismissReason = Schemas["PurchasingReorderDismissReason"];

export type PurchasingConfiguration = Schemas["PurchasingConfiguration"];

/** Cross-domain lookups this feature reads (never writes). */
export type PartySummary = Schemas["PartnersPartySummary"];
export type Variant = Schemas["CatalogVariant"];
export type VariantBarcode = Schemas["CatalogBarcode"];
export type Uom = Schemas["CatalogUom"];
export type Warehouse = Schemas["InventoryWarehouse"];
export type WarehouseLocation = Schemas["InventoryWarehouseLocation"];
export type Batch = Schemas["InventoryBatch"];

// ── Enum tuples (exhaustive against the wire enums) ──────────────────────

export const PURCHASE_ORDER_STATUSES = [
  "draft",
  "pending_approval",
  "approved",
  "sent",
  "partially_received",
  "received",
  "closed",
] as const satisfies readonly PurchaseOrderStatus[];

export const LANDED_COST_STATUSES = [
  "draft",
  "posted",
] as const satisfies readonly LandedCostStatus[];

export const LANDED_COST_TYPES = [
  "freight",
  "duty",
  "insurance",
  "other",
] as const satisfies readonly LandedCostType[];

export const LANDED_COST_BASES = [
  "value",
  "weight",
  "quantity",
] as const satisfies readonly LandedCostBasis[];

export const REORDER_SUGGESTION_STATUSES = [
  "open",
  "converted",
  "dismissed",
  "superseded",
] as const satisfies readonly ReorderSuggestionStatus[];

export const REORDER_DISMISS_REASONS = [
  "ordered_elsewhere",
  "temporary_exception",
  "not_required",
  "data_issue",
] as const satisfies readonly ReorderDismissReason[];

export const TRANSMISSION_STATUSES = [
  "queued",
  "sent",
  "failed",
] as const satisfies readonly TransmissionStatus[];

/** Statuses that may still receive stock (server remains authoritative). */
export const RECEIVABLE_STATUSES: readonly PurchaseOrderStatus[] = [
  "approved",
  "sent",
  "partially_received",
];

// ── Query aliases ────────────────────────────────────────────────────────

export type PurchaseOrderListQuery = NonNullable<
  operations["listPurchasingPurchaseOrders"]["parameters"]["query"]
>;
export type GrnListQuery = NonNullable<operations["listPurchasingGrns"]["parameters"]["query"]>;
export type LandedCostListQuery = NonNullable<
  operations["listPurchasingLandedCosts"]["parameters"]["query"]
>;
export type SupplierItemListQuery = NonNullable<
  operations["listPurchasingSupplierItems"]["parameters"]["query"]
>;
export type ReorderSuggestionListQuery = NonNullable<
  operations["listPurchasingReorderSuggestions"]["parameters"]["query"]
>;
export type TransmissionListQuery = NonNullable<
  operations["listPurchasingPurchaseOrderTransmissions"]["parameters"]["query"]
>;

/** Allowlisted sort tokens, straight off the contract's enums. */
export type PurchaseOrderSort = NonNullable<PurchaseOrderListQuery["sort"]>;
export type GrnSort = NonNullable<GrnListQuery["sort"]>;
export type LandedCostSort = NonNullable<LandedCostListQuery["sort"]>;
export type SupplierItemSort = NonNullable<SupplierItemListQuery["sort"]>;

type GeneratedPage<T> = Schemas["PageEnvelope"] & { readonly items: readonly T[] };

function toMutablePage<T>(page: GeneratedPage<T>): Page<T> {
  return { items: [...page.items], next_cursor: page.next_cursor };
}

/**
 * Purchasing sorts are a single allowlisted token with a `-` descending
 * prefix — NOT the app-wide `key:dir` spelling — so DataTable's SortState is
 * serialized here rather than through `toListParams`.
 */
export function toSortToken<T extends string>(
  sort: { key: string; dir: "asc" | "desc" } | null | undefined,
  allowed: readonly T[],
): T | undefined {
  if (sort === null || sort === undefined) return undefined;
  const token = (sort.dir === "desc" ? `-${sort.key}` : sort.key) as T;
  return allowed.includes(token) ? token : undefined;
}

export const PURCHASE_ORDER_SORTS = [
  "created_at",
  "-created_at",
  "order_date",
  "-order_date",
  "expected_delivery_date",
  "-expected_delivery_date",
  "po_number",
  "-po_number",
] as const satisfies readonly PurchaseOrderSort[];

export const GRN_SORTS = [
  "received_at",
  "-received_at",
  "grn_number",
  "-grn_number",
  "created_at",
  "-created_at",
] as const satisfies readonly GrnSort[];

export const LANDED_COST_SORTS = [
  "created_at",
  "-created_at",
  "document_number",
  "-document_number",
  "posted_at",
  "-posted_at",
] as const satisfies readonly LandedCostSort[];

export const SUPPLIER_ITEM_SORTS = [
  "updated_at",
  "-updated_at",
  "supplier_sku",
  "-supplier_sku",
  "lead_time_days",
  "-lead_time_days",
] as const satisfies readonly SupplierItemSort[];

// ── Purchase orders ──────────────────────────────────────────────────────

export async function listPurchaseOrders(
  query: PurchaseOrderListQuery,
  signal?: AbortSignal,
): Promise<Page<PurchaseOrderSummary>> {
  return toMutablePage(
    await apiFetch<GeneratedPage<PurchaseOrderSummary>>("/purchase-orders", {
      query: { ...query },
      signal,
    }),
  );
}

/** Detail + ETag; the ETag is the If-Match validator for every PO write. */
export function getPurchaseOrder(purchaseOrderId: string): Promise<WithEtag<PurchaseOrder>> {
  return fetchWithEtag<PurchaseOrder>(`/purchase-orders/${purchaseOrderId}`);
}

export async function createPurchaseOrder(
  input: PurchaseOrderCreate,
  idempotencyKey: string,
): Promise<WithEtag<PurchaseOrder>> {
  const { data, etag } = await apiRequest<PurchaseOrder>("/purchase-orders", {
    method: "POST",
    body: input,
    idempotencyKey,
  });
  return { data, etag };
}

export async function updatePurchaseOrder(
  purchaseOrderId: string,
  input: PurchaseOrderUpdate,
  ifMatch: string,
): Promise<WithEtag<PurchaseOrder>> {
  const { data, etag } = await apiRequest<PurchaseOrder>(`/purchase-orders/${purchaseOrderId}`, {
    method: "PUT",
    body: input,
    ifMatch,
  });
  return { data, etag };
}

export function deletePurchaseOrder(purchaseOrderId: string): Promise<void> {
  return apiFetch<void>(`/purchase-orders/${purchaseOrderId}`, {
    method: "DELETE",
  });
}

export function getApprovalPreview(purchaseOrderId: string): Promise<ApprovalPreview> {
  return apiFetch<ApprovalPreview>(`/purchase-orders/${purchaseOrderId}/approval-preview`);
}

export async function submitPurchaseOrder(
  purchaseOrderId: string,
  ifMatch: string,
  idempotencyKey: string,
): Promise<WithEtag<PurchaseOrder>> {
  const { data, etag } = await apiRequest<PurchaseOrder>(
    `/purchase-orders/${purchaseOrderId}/submit`,
    { method: "POST", ifMatch, idempotencyKey },
  );
  return { data, etag };
}

export async function sendPurchaseOrder(
  purchaseOrderId: string,
  input: SendPurchaseOrderRequest,
  ifMatch: string,
  idempotencyKey: string,
): Promise<WithEtag<SendPurchaseOrderResult>> {
  const { data, etag } = await apiRequest<SendPurchaseOrderResult>(
    `/purchase-orders/${purchaseOrderId}/send`,
    { method: "POST", body: input, ifMatch, idempotencyKey },
  );
  return { data, etag };
}

export async function closePurchaseOrder(
  purchaseOrderId: string,
  ifMatch: string,
  idempotencyKey: string,
): Promise<WithEtag<PurchaseOrder>> {
  const body: ClosePurchaseOrderRequest = { close_mode: "complete" };
  const { data, etag } = await apiRequest<PurchaseOrder>(
    `/purchase-orders/${purchaseOrderId}/close`,
    { method: "POST", body, ifMatch, idempotencyKey },
  );
  return { data, etag };
}

export async function listTransmissions(
  purchaseOrderId: string,
  query: TransmissionListQuery,
  signal?: AbortSignal,
): Promise<Page<Transmission>> {
  return toMutablePage(
    await apiFetch<GeneratedPage<Transmission>>(
      `/purchase-orders/${purchaseOrderId}/transmissions`,
      { query: { ...query }, signal },
    ),
  );
}

// ── Goods receipts ───────────────────────────────────────────────────────

export async function listGrns(
  query: GrnListQuery,
  signal?: AbortSignal,
): Promise<Page<GrnSummary>> {
  return toMutablePage(
    await apiFetch<GeneratedPage<GrnSummary>>("/grns", { query: { ...query }, signal }),
  );
}

export function getGrn(grnId: string): Promise<Grn> {
  return apiFetch<Grn>(`/grns/${grnId}`);
}

/** Creates AND posts the receipt in one server transaction (contract §POST /grns). */
export function createGrn(input: GrnCreate, idempotencyKey: string): Promise<GrnCreateResult> {
  return apiFetch<GrnCreateResult>("/grns", { method: "POST", body: input, idempotencyKey });
}

// ── Landed costs ─────────────────────────────────────────────────────────

export async function listLandedCosts(
  query: LandedCostListQuery,
  signal?: AbortSignal,
): Promise<Page<LandedCostDocumentSummary>> {
  return toMutablePage(
    await apiFetch<GeneratedPage<LandedCostDocumentSummary>>("/landed-costs", {
      query: { ...query },
      signal,
    }),
  );
}

export function getLandedCost(landedCostId: string): Promise<WithEtag<LandedCostDocument>> {
  return fetchWithEtag<LandedCostDocument>(`/landed-costs/${landedCostId}`);
}

export async function createLandedCost(
  input: LandedCostDocumentCreate,
  idempotencyKey: string,
): Promise<WithEtag<LandedCostDocument>> {
  const { data, etag } = await apiRequest<LandedCostDocument>("/landed-costs", {
    method: "POST",
    body: input,
    idempotencyKey,
  });
  return { data, etag };
}

export async function updateLandedCost(
  landedCostId: string,
  patch: LandedCostDocumentPatch,
  ifMatch: string,
): Promise<WithEtag<LandedCostDocument>> {
  const { data, etag } = await apiRequest<LandedCostDocument>(`/landed-costs/${landedCostId}`, {
    method: "PATCH",
    body: patch,
    ifMatch,
    headers: { "Content-Type": "application/merge-patch+json" },
  });
  return { data, etag };
}

export function deleteLandedCost(landedCostId: string): Promise<void> {
  return apiFetch<void>(`/landed-costs/${landedCostId}`, {
    method: "DELETE",
  });
}

/**
 * Side-effect-free allocation dry run. The policy declares no idempotency
 * key for it, so the transport must not attach one.
 */
export function previewLandedCost(
  landedCostId: string,
  input: LandedCostPreviewRequest,
): Promise<LandedCostPreview> {
  return apiFetch<LandedCostPreview>(`/landed-costs/${landedCostId}/preview`, {
    method: "POST",
    body: input,
    idempotency: "none",
  });
}

export async function postLandedCost(
  landedCostId: string,
  input: LandedCostPostRequest,
  ifMatch: string,
  idempotencyKey: string,
): Promise<WithEtag<LandedCostPostResult>> {
  const { data, etag } = await apiRequest<LandedCostPostResult>(
    `/landed-costs/${landedCostId}/post`,
    { method: "POST", body: input, ifMatch, idempotencyKey },
  );
  return { data, etag };
}

// ── Supplier items ───────────────────────────────────────────────────────

export async function listSupplierItems(
  query: SupplierItemListQuery,
  signal?: AbortSignal,
): Promise<Page<SupplierItem>> {
  return toMutablePage(
    await apiFetch<GeneratedPage<SupplierItem>>("/supplier-items", {
      query: { ...query },
      signal,
    }),
  );
}

export function getSupplierItem(supplierItemId: string): Promise<WithEtag<SupplierItem>> {
  return fetchWithEtag<SupplierItem>(`/supplier-items/${supplierItemId}`);
}

export function createSupplierItem(
  input: SupplierItemCreate,
  idempotencyKey: string,
): Promise<SupplierItem> {
  return apiFetch<SupplierItem>("/supplier-items", {
    method: "POST",
    body: input,
    idempotencyKey,
  });
}

export async function updateSupplierItem(
  supplierItemId: string,
  input: SupplierItemUpdate,
  ifMatch: string,
): Promise<WithEtag<SupplierItem>> {
  const { data, etag } = await apiRequest<SupplierItem>(`/supplier-items/${supplierItemId}`, {
    method: "PUT",
    body: input,
    ifMatch,
  });
  return { data, etag };
}

export function deleteSupplierItem(supplierItemId: string, ifMatch: string): Promise<void> {
  return apiFetch<void>(`/supplier-items/${supplierItemId}`, {
    method: "DELETE",
    ifMatch,
  });
}

// ── Reorder suggestions ──────────────────────────────────────────────────

export async function listReorderSuggestions(
  query: ReorderSuggestionListQuery,
  signal?: AbortSignal,
): Promise<Page<ReorderSuggestion>> {
  return toMutablePage(
    await apiFetch<GeneratedPage<ReorderSuggestion>>("/reorder-suggestions", {
      query: { ...query },
      signal,
    }),
  );
}

/**
 * `GET /reorder-suggestions/{id}` is deliberately not wired to a screen: the
 * list returns the identical full `PurchasingReorderSuggestion` shape, so a
 * per-row detail fetch would add a round-trip and show nothing new.
 */

export function convertReorderSuggestions(
  input: ReorderConversionRequest,
  idempotencyKey: string,
): Promise<ReorderConversionResult> {
  return apiFetch<ReorderConversionResult>("/reorder-suggestions/convert", {
    method: "POST",
    body: input,
    idempotencyKey,
  });
}

/** `calculatePurchasingReorderSuggestions` — POST · client-key. Answers 202:
 *  the run was accepted, not finished. Its suggestions appear in the list when
 *  the job completes, stamped with the run key returned here. */
export function calculateReorderSuggestions(
  idempotencyKey: string,
): Promise<{ run_key: string; calculated_at: string }> {
  return apiFetch<{ run_key: string; calculated_at: string }>("/reorder-suggestions/calculate", {
    method: "POST",
    idempotencyKey,
  });
}

export function dismissReorderSuggestions(
  input: ReorderDismissRequest,
  idempotencyKey: string,
): Promise<ReorderDismissResult> {
  return apiFetch<ReorderDismissResult>("/reorder-suggestions/dismiss", {
    method: "POST",
    body: input,
    idempotencyKey,
  });
}

// ── Configuration ────────────────────────────────────────────────────────

export function getPurchasingConfiguration(signal?: AbortSignal): Promise<PurchasingConfiguration> {
  return apiFetch<PurchasingConfiguration>("/purchasing/config", { signal });
}

// ── Cross-domain lookups (read-only) ─────────────────────────────────────

/** Supplier parties for pickers and name resolution. */
export async function listSupplierParties(
  q: string | undefined,
  signal?: AbortSignal,
): Promise<PartySummary[]> {
  const page = await apiFetch<GeneratedPage<PartySummary>>("/parties", {
    query: {
      role: "supplier",
      status: "active",
      limit: 200,
      ...(q === undefined || q === "" ? {} : { q }),
    },
    signal,
  });
  return [...page.items];
}

export async function listVariants(
  q: string | undefined,
  signal?: AbortSignal,
): Promise<Variant[]> {
  const page = await apiFetch<GeneratedPage<Variant>>("/variants", {
    query: { limit: 200, ...(q === undefined || q === "" ? {} : { q }) },
    signal,
  });
  return [...page.items];
}

/**
 * Contract-published barcode filter on the variant collection. The deployed
 * catalog service currently refuses this parameter (see the barcode-index
 * fallback below and the backend gap in the lane report), so callers must
 * treat a failure as "unresolved" rather than fatal.
 */
export async function lookupVariantsByBarcode(
  barcode: string,
  signal?: AbortSignal,
): Promise<Variant[]> {
  const page = await apiFetch<GeneratedPage<Variant>>("/variants", {
    query: { barcode, limit: 200 },
    signal,
  });
  return [...page.items];
}

/** Barcodes assigned to one variant (`catalog.barcode.read`). */
export async function listVariantBarcodes(
  variantId: string,
  signal?: AbortSignal,
): Promise<VariantBarcode[]> {
  const page = await apiFetch<GeneratedPage<VariantBarcode>>(`/variants/${variantId}/barcodes`, {
    query: { limit: 200 },
    signal,
  });
  return [...page.items];
}

export async function listUoms(signal?: AbortSignal): Promise<Uom[]> {
  const page = await apiFetch<GeneratedPage<Uom>>("/uoms", {
    query: { status: "active", limit: 200 },
    signal,
  });
  return [...page.items];
}

/** Warehouses and locations are branch-scoped operations (route policy). */
export async function listWarehouses(signal?: AbortSignal): Promise<Warehouse[]> {
  const page = await apiFetchOperational<GeneratedPage<Warehouse>>("/warehouses", {
    query: { limit: 200 },
    signal,
  });
  return [...page.items];
}

export async function listWarehouseLocations(
  warehouseId: string,
  signal?: AbortSignal,
): Promise<WarehouseLocation[]> {
  const page = await apiFetchOperational<GeneratedPage<WarehouseLocation>>(
    `/warehouses/${warehouseId}/locations`,
    { query: { limit: 200 }, signal },
  );
  return [...page.items];
}

export async function listBatches(
  variantId: string,
  warehouseId: string | undefined,
  signal?: AbortSignal,
): Promise<Batch[]> {
  const page = await apiFetchOperational<GeneratedPage<Batch>>("/batches", {
    query: {
      variant_id: variantId,
      limit: 200,
      ...(warehouseId === undefined ? {} : { warehouse_id: warehouseId }),
    },
    signal,
  });
  return [...page.items];
}
